// === Enums ===

export enum ShowingStatus {
  Reserved = "reserved",
  Pending = "pending",
  Confirmed = "confirmed",
  LeadConfirmed = "lead_confirmed",
  InProgress = "in_progress",
  Completed = "completed",
  NoShow = "no_show",
  Cancelled = "cancelled",
  Expired = "expired",
  Ongoing = "ongoing"
}

export enum ShowingType {
  Guided = "guided",
  SelfGuided = "self_guided"
}

export enum ShowingFeedback {
  VeryInterested = "very_interested",
  Interested = "interested",
  Neutral = "neutral",
  NotInterested = "not_interested"
}

// === Scheduling Settings ===

export interface SchedulingSettings {
  id: number;
  partner_id: number;
  showing_type: ShowingType;
  duration_minutes: number;
  available_days_in_future: number | null;
  require_confirmation: boolean;
  timezone: string;
  minimum_scheduling_notice_minutes: number;
  max_visits_per_unit_daily: number | null;
  max_visits_per_unit_weekly: number | null;
  only_schedule_assigned_user: boolean;
  intelligent_scheduling_enabled: boolean;
  drive_time_buffer_percent: number | null;
  checkin_link_lead_time_minutes: number | null;
  checkout_reminder_minutes_before_end: number | null;
  require_geolocation_validation: boolean;
  no_show_alert_minutes: number | null;
  send_confirmation_before_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export type UpdateSchedulingSettingsPayload = Partial<
  Omit<
    SchedulingSettings,
    "id" | "partner_id" | "showing_type" | "created_at" | "updated_at"
  >
>;

// === Showing Settings ===

export interface BaseAddress {
  address1: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressEntity {
  address1: string;
  address2: string | null;
  city: string;
  state_or_province: string;
  postal_code: string;
  country: string;
  lat: number | null;
  lng: number | null;
  one_line_address: string;
}

export interface ShowingSettings {
  id: number;
  partner_user_id: number;
  buffer_minutes: number;
  scheduling_limit_days: number | null;
  timezone: string;
  default_reminder_minutes: number | null;
  base_address: AddressEntity | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateShowingSettingsPayload {
  buffer_minutes?: number;
  scheduling_limit_days?: number | null;
  timezone?: string;
  default_reminder_minutes?: number | null;
  base_address?: BaseAddress;
}

// === Weekly Availability ===

export interface AvailabilityRule {
  id: number;
  partner_user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface ReplaceAvailabilityPayload {
  availabilities: Omit<AvailabilityRule, "id" | "partner_user_id">[];
}

// === Blocked Days ===

export interface BlockedDay {
  id: number;
  partner_user_id: number;
  blocked_date: string;
  reason: string | null;
}

export interface AddBlockedDayPayload {
  blocked_date: string;
  reason?: string;
}

// === Date Overrides ===

export interface TimeWindow {
  start_time: string;
  end_time: string;
}

export interface DateOverride {
  id: number;
  partner_user_id: number;
  override_date: string;
  unavailable_all_day: boolean;
  start_time?: string;
  end_time?: string;
  created_at: string;
}

export interface SetDateOverridePayload {
  override_date: string;
  unavailable_all_day?: boolean;
  windows?: TimeWindow[];
}

export interface RemoveDateOverridePayload {
  override_date: string;
}

// === Calendar ===

export interface CalendarParams {
  start_date: string;
  end_date: string;
  timezone?: string;
  property_ids?: number[];
  unit_ids?: number[];
  partner_user_ids?: number[];
  statuses?: ShowingStatus[];
  showing_types?: ShowingType[];
}

export interface ExternalEvent {
  title: string;
  email: string;
  start_time: string;
  end_time: string;
  provider: string;
  all_day: boolean;
}

export interface CalendarResponse {
  timezone: string;
  showings: Showing[];
  external_events: ExternalEvent[];
  blocked_days: BlockedDay[];
  date_overrides: DateOverride[];
  weekly_availability: AvailabilityRule[];
}

// === Nested Objects ===

export interface PartnerUserRef {
  id: number;
  name: string;
  email: string;
}

export interface PropertyRef {
  id: number;
  name: string;
}

export interface UnitRef {
  id: number;
  title: string;
}

export interface LeadRef {
  id: number;
  first_name: string;
  last_name: string;
  primary_email: string;
  primary_phone: string | null;
}

// === Showings ===

export interface Showing {
  id: number;
  showing_type: ShowingType;
  status: ShowingStatus;
  start_time: string;
  end_time: string;
  timezone: string | null;
  desired_move_in_date: string | null;
  message: string | null;
  feedback: ShowingFeedback | null;
  cancelled_by_type: string | null;
  cancelled_by_id: number | null;
  cancellation_reason: string | null;
  cancelled_at: string | null;
  confirmed_at: string | null;
  reserved_at: string | null;
  reservation_expires_at: string | null;
  confirmation_expires_at: string | null;
  rescheduled_from_id: number | null;
  reschedule_count: number;
  partner_user: PartnerUserRef | null;
  booked_by_partner_user: PartnerUserRef | null;
  property: PropertyRef;
  unit: UnitRef | null;
  lead: LeadRef | null;
  lockbox_code: string | null;
  lead_checkout_feedback: string | null;
  created_at: string;
  updated_at: string;
}

export interface PropertyContact {
  company_name: string;
  phone: string;
}

export interface ShowingNote {
  id: number;
  body: string;
  partner_user: PartnerUserRef;
  created_at: string;
}

export interface ShowingPropertyAddress {
  address1: string;
  address2?: string | null;
  city: string;
  state_or_province: string;
  postal_code: string;
  country?: string;
  lat?: number | null;
  lng?: number | null;
  one_line_address: string;
}

export interface ShowingDetail extends Showing {
  property_address: ShowingPropertyAddress;
  property_contacts: PropertyContact | null;
  notes: ShowingNote[];
}

// === Showing Payloads ===

export interface ReserveSlotPayload {
  property_id: number;
  start_time: string;
  end_time: string;
  lead_id?: number;
  lead_email?: string;
  unit_id?: number;
  showing_type?: ShowingType;
}

export interface BookFromReservationPayload {
  showing_id: number;
  lead_id?: number;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
  desired_move_in_date?: string;
  message?: string;
  booked_by_partner_user_id?: number;
}

export interface BookDirectPayload {
  property_id: number;
  start_time: string;
  end_time: string;
  unit_id?: number;
  lead_id?: number;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
  desired_move_in_date?: string;
  message?: string;
  showing_type?: ShowingType;
  booked_by_partner_user_id?: number;
  assignee_id?: number;
}

export interface CancelShowingPayload {
  cancellation_reason?: string;
}

export interface RescheduleShowingPayload {
  new_start_time: string;
  new_end_time: string;
}

export interface AddFeedbackPayload {
  feedback: ShowingFeedback;
}

export interface ReassignShowingPayload {
  partner_user_id: number;
}

export interface AddNotePayload {
  note_content: string;
}

// === Availability Slots ===

export interface AvailabilitySlot {
  available_count: number;
  date: string;
  start_time: string;
  end_time: string;
  partner_user_ids: number[];
}

export interface AvailabilitySlotParams {
  property_id: number;
  unit_id?: number | null;
  lead_id?: number;
  partner_user_id?: number;
  showing_type?: ShowingType;
}

// === Showing Rotation ===

export interface ShowingRotationSlot {
  partner_user_id: number;
  name: string;
  position: number;
  is_next: boolean;
  paused: boolean;
  pause_reason: string | null;
  showings_today: number;
  showings_this_week: number;
}

// === List Showings ===

export interface ListShowingsParams {
  property_id?: number;
  unit_id?: number;
  partner_user_id?: number;
  status?: ShowingStatus[];
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}

export interface ListShowingsResponse {
  items: Showing[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
}

// === Lead Section Showings (lean payload from GET /leads/:id/showings) ===

export interface LeadShowing {
  id: number;
  status: ShowingStatus;
  showing_type: ShowingType;
  feedback: ShowingFeedback | null;
  start_time: string;
  end_time: string;
  timezone: string | null;
  partner_user: PartnerUserRef | null;
  property: PropertyRef;
  unit: UnitRef | null;
  property_address: ShowingPropertyAddress | null;
}

export interface LeadShowingsResponse {
  showings: LeadShowing[];
  total_count: number;
}
